import { AdTemplate } from '@/components/AdTemplate';
import { AuthorDetails } from '@/components/AuthorDetails';
import { Base } from '@/components/Base';
import { Consent } from '@/components/Consent';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { Search } from '@/components/Search';
import { SoMeProps } from '@/components/SoMe';
import { Tracking } from '@/components/Tracking';
import { BylineData } from '@/types/bylineData';
import getUrl from '@/utils/getUrl';
import { fetchAuthorArticles } from 'base/components/Search';
import { loadAdsData } from 'lib/data';
import { Author, getAuthorBySlug } from 'lib/labrador/author-page';
import { getConfigObject } from 'lib/labrador/config-object';
import { AdsMeta } from 'lib/labrador/types';
import getCurrentBrand from 'lib/utils/getCurrentBrand';
import { withSerializationGuard } from 'lib/utils/withSerializationGuard';
import getConfig from 'modules/config';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { isTruthy } from 'typesafe-utils';

interface PageAuthorsProps {
  author: Author;
  searchResults?: BylineData;
  adsData?: AdsMeta;
  titleSuffix?: string;
}

const PageAuthors: NextPage<PageAuthorsProps> = ({ author, searchResults, adsData, titleSuffix }) => {
  const src = author?.imageBig?.replace('%3F', '?');
  const authorName = `${author.firstName} ${author.lastName}`;
  const brandName = getCurrentBrand().name;
  const publishedUrl = `/vara-skribenter/${author.slug}`;
  const canonical = getUrl(publishedUrl)?.href;

  const soMeList = [
    {
      name: 'facebook',
      href: author.facebook,
    },
    {
      name: 'x',
      href: author.twitter,
    },
    {
      name: 'instagram',
      href: author.instagram,
    },
  ].filter(({ href }) => isTruthy(href)) as SoMeProps[];

  const description = `Artiklar skrivna av ${authorName}. Läs alla artiklar av ${authorName} på ${brandName}.`;

  return (
    <Base
      content={
        <>
          <Head>
            <title>{`${authorName} ${titleSuffix}`}</title>
            <meta name="title" content={`${authorName} ${titleSuffix}`} key="metaTitle" />
            <meta name="description" content={description} key="metaDescription" />
            <link rel="canonical" href={canonical} />
          </Head>
          <Consent />
          <AdTemplate adsData={adsData} />
          <Tracking />
          <SchemaMarkup
            pageData={{
              type: 'front',
              meta: {},
              data: {
                publishedUrl,
                seoTitle: authorName,
                seoDescription: description,
              },
              children: [],
            }}
          />

          <AuthorDetails
            title={authorName}
            image={{ src, alt: authorName }}
            description={author.description}
            soMeList={soMeList}
            caption={
              <>
                {author.title && <p>{author.title}</p>}
                {author.publicEmail && <a href={`mailto:${author.publicEmail}`}>{author.publicEmail}</a>}
              </>
            }
          />

          <Search
            total={searchResults?.totalItems}
            query={`${authorName}`}
            adsUniqueId={adsData?.adsUniqueId}
            initial={{ next: searchResults?.nextPage, results: searchResults?.items }}
            pageType="authorPage"
          />
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  // If author-page is not enabled we return a 404
  const authorPageEnabled = getConfig('authorPage.enabled') || false;
  if (!authorPageEnabled) return { notFound: true };

  const authorSlug = (context.params?.author || '') as string;
  const route = ['vara-skribenter', authorSlug];

  // Fetch our current author
  const author = await getAuthorBySlug(authorSlug);

  if (!author) return { notFound: true };

  const query = author.fullName;
  const authorData = await fetchAuthorArticles(query);

  const adsData = await loadAdsData();

  const titleSuffix = await getConfigObject('miscsettings.site.siteTitleSuffix');

  return {
    props: withSerializationGuard({
      key: route.join('/'),
      author,
      searchResults: { query: query ?? null, ...authorData },
      adsData,
      titleSuffix,
    }),
  };
};

export default PageAuthors;
